import every from 'lodash/every';
import { HQ_USER_TYPE, HQ_COMPANY_TYPE, HQ_ROLE_TYPE } from '../constants/hq-types';
import intersection from 'lodash/intersection';
import { ALL_USERS_SPECIAL_ID } from '../constants/roles-types';
import T from 'i18n-react';
import { sortAlphabeticallyByPath } from './sortingHelpers';
import { findAssigneeInOxygenMappingById } from './userHelpers';

const entityTypeToPlural = {
  [HQ_USER_TYPE]: 'users',
  [HQ_COMPANY_TYPE]: 'companies',
  [HQ_ROLE_TYPE]: 'roles',
};

export const getOxygenEntityInfo = function(oxygen, type, id) {
  return (oxygen[entityTypeToPlural[type]] || {})[id] || null;
};

// eslint-disable-next-line consistent-return
export const compareText = function(a, b) {
  if (typeof a.text === 'string' && typeof b.text === 'string') {
    return a.text.toLowerCase().localeCompare(b.text.toLowerCase());
  }
};

// side-effect-free, pure function, that does not need to be an instance method:
export const assigneesForSelect = function(users, companies, roles) {
  return [
    {
      text: T.translate('assignment.user'),
      type: HQ_USER_TYPE,
      children: Object.keys(users).map(id => {return { id, text: users[id].name, title: users[id].email };}).sort(compareText),
    },
    {
      text: T.translate('assignment.company'),
      type: HQ_COMPANY_TYPE,
      children: Object.keys(companies).map(id => {return { id, text: companies[id].name };}).sort(compareText),
    },
    {
      text: T.translate('assignment.role'),
      type: HQ_ROLE_TYPE,
      children: Object.keys(roles).map(id => {return { id, text: roles[id].name };}).sort(compareText),
    },
  ];
};

export const getSimpleGroupedEntities = (users, companies, roles) => {
  const groupedEntities = [];
  // Users
  groupedEntities.push([
    T.translate('assignment.user'),
    users,
  ]);
  // Roles
  groupedEntities.push([
    T.translate('assignment.role'),
    roles,
  ]);
  // Companies
  groupedEntities.push([
    T.translate('assignment.company'),
    companies,
  ]);

  return every(groupedEntities, group => group[1].length === 0) ? [] : groupedEntities;
};

export const assigneeObjectToValue = (assigneeObj) => {
  return `${assigneeObj.type}:${assigneeObj.id}`;
};

/**
 * Get users list based on users, roles & companies.
 * This method returns a list of all users that either exists in assigneesArray or their role or company exits there.
 * @param assigneesArray - array of users, roles & companies.
 * @param oxygen
 */

export const getUsersList = (assigneesArray, oxygen) => {
  const userIds = assigneesArray.filter(obj => obj.type === HQ_USER_TYPE).map(obj => obj.id);
  const roleIds = assigneesArray.filter(obj => obj.type === HQ_ROLE_TYPE).map(obj => obj.id);
  const companyIds = assigneesArray.filter(obj => obj.type === HQ_COMPANY_TYPE).map(obj => obj.id);

  return sortAlphabeticallyByPath(Object.keys(oxygen.users)
    .filter(userId => {
      const user = oxygen.users[userId];
      return userIds.includes(ALL_USERS_SPECIAL_ID) || userIds.includes(userId) || user.company && companyIds.includes(user.company.oxygenId) || intersection(roleIds, user.roles.map(role => role.oxygenId)).length;
    }).map((userId) => oxygen.users[userId]), 'name');
};

export const isSingleUserAssignee = (assignees) => {
  return assignees && assignees.length === 1 && assignees[0].type === HQ_USER_TYPE;
};

export const isSingleCompanyAssignee = (assignees) => {
  return assignees && assignees.length === 1 && assignees[0].type === HQ_COMPANY_TYPE;
};

// Assignees can come from:
// SelectDropdown  - which means we get the entire assignee object as part of an array and we don't need to do anything
// Url query params - which means that we get either one string (in case of one assignee) or an array of strings. For this scenario we need to find the entity from oxygenMapping
export const getAssignees = (rawAssignees, oxygenMapping) => {
  const assignees = rawAssignees.constructor === Array ? [].concat(rawAssignees) : [rawAssignees];

  for (let i = 0; i < assignees.length; i++) {
    const currentAssignee = assignees[i];
    // If currentAssignee is string it means that it's just the Id and not the actual assignee object so we need to find it in the oxygenMapping
    // Otherwise, we don't need to mutate assignees[i] since it already holds the assignee object
    if (currentAssignee.constructor === String) {
      assignees[i] = findAssigneeInOxygenMappingById(currentAssignee, oxygenMapping);
    }
  }

  return assignees;
};
